import { Component, ChangeDetectorRef } from '@angular/core';
import { BaseMortgageApplicationComponent } from '../../../pages/base-mortgage-application.component';
import { PropertyOccupant } from './../../../models/property-occupant.model';
import { MortgageApplicationStore } from './../../../services/mortgage-application-store';
import { ReferenceDataService } from '../../../../../lib/services/reference-data.service';
import { MortgageApplicationFormService } from '../../../services/mortgage-application-form.service';
import { MortgageApplicationContext } from '../../../models/mortgage-application-context.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReferenceDataCategoriesConstants } from '../../../../../lib/constants/reference-data-categories';
import { NotifyChangeDetectionService } from '../../../../../lib/services/notify-change-detection.service';
import { ReferenceData } from '../../../../../lib/models/reference-data.model';
import { moveConfigItemToFirstPosition } from '../../../../../lib/functions/generic';
import { ReferenceDataConstants } from '../../../../../lib/constants/reference-data-constants';

@UntilDestroy()
@Component({
  selector: 'boi-your-property-details',
  templateUrl: './your-property-details.component.html',
  styleUrls: ['./your-property-details.component.scss']
})
export class YourPropertyDetailsComponent extends BaseMortgageApplicationComponent {
  public referenceDataConstants = ReferenceDataConstants;
  private readonly maxPropertyOccupants = 50;
  public displayEPCDateIssuedField: boolean;
  public EPCRatingOptionList: ReferenceData[];
  public epcRatingInfoMessage: string;
  public isEPCRatingOptionListDisabled = false;
  public isEPCEPCDateDisabled = false;
  public isEPCRatingAmendDisplayed = false;
  public isPEARating = false;
  public isEPCExpiredOnRemortgage = false;
  public EPCRatingTooltip: string;
  public isNotEPCRatingOnCurrentProduct: boolean;

  constructor(
    protected mortgageApplicationStore: MortgageApplicationStore,
    private readonly mortgageApplicationFormService: MortgageApplicationFormService,
    private readonly referenceDataService: ReferenceDataService,
    private readonly notifyChangeDetectionService: NotifyChangeDetectionService,
    private readonly cdr: ChangeDetectorRef
  ) {
    super(mortgageApplicationStore);
    this.notifyChangeDetectionService
      .getSubject()
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.cdr.detectChanges();
        this.onCheckPropertyYearOfConstruction();
      });
  }

  protected initialize(): void {
    this.setEPCRatingOptionList();
    this.handleEPCRatingEPCDateAmendButtonDisplay();
    this.onCheckPropertyYearOfConstruction();

    if (
      this.mortgageApplicationFormService.getCurrentContext() === MortgageApplicationContext.Direct &&
      this.mortgageApplication.IsFullApp &&
      this.mortgageApplication.isAdvisedCase
    ) {
      this.EPCRatingTooltip = ReferenceDataConstants.EpcRatingContactAdviserInfo;
    } else {
      this.EPCRatingTooltip = this.referenceDataLabels.EPCRating;
    }
  }

  isRelationshipToYouDetailsVisible(propertyOccupant: PropertyOccupant): boolean {
    if (propertyOccupant.RelationshipWithApplicantCode === this.referenceDataOptionLists.RelationshipTypeOther) {
      return true;
    }

    return false;
  }

  nullifyPropertyOccupantRelationshipDetails(propertyOccupant: PropertyOccupant): void {
    if (!this.isRelationshipToYouDetailsVisible(propertyOccupant)) {
      propertyOccupant.RelationshipToYouDetails = undefined;
    }
  }

  initializePropertyOccupant(): void {
    if (this.mortgageApplication.IsFullApp) {
      if (this.mortgageApplication.NoPeopleAgedOver17 <= this.maxPropertyOccupants) {
        this.handlePropertyOccupantsList();
      } else {
        this.mortgageApplication.PropertyOccupants = [];
        // To do: Implement numberOfOccupantsInvalid, show error notification
      }
    }
  }

  private handlePropertyOccupantsList(): void {
    if (this.mortgageApplication.NoPeopleAgedOver17 === 0 || !this.mortgageApplication.PropertyOccupants) {
      this.mortgageApplication.PropertyOccupants = [];
    }
    for (
      let i = this.mortgageApplication.PropertyOccupants.length;
      i < this.mortgageApplication.NoPeopleAgedOver17;
      i++
    ) {
      this.mortgageApplication.PropertyOccupants.push(new PropertyOccupant());
    }
    if (this.mortgageApplication.NoPeopleAgedOver17 < this.mortgageApplication.PropertyOccupants.length) {
      this.mortgageApplication.PropertyOccupants = this.mortgageApplication.PropertyOccupants.slice(
        0,
        this.mortgageApplication.NoPeopleAgedOver17
      );
    }
  }

  public onAmendClick(): void {
    this.mortgageApplicationStore.notifyAmendProduct();
  }

  private setEPCRatingOptionList(): void {
    if (this.mortgageApplication.isPurchase) {
      if (this.mortgageApplication.isResidential) {
        this.EPCRatingOptionList = this.referenceDataService.getOptionListConfigItems(
          this.referenceDataOptionLists.EPCRatingResidentialPurchase
        );
      } else {
        this.EPCRatingOptionList = this.referenceDataService.getOptionListConfigItems(
          this.referenceDataOptionLists.EPCRatingBTLPurchase
        );
      }
    } else {
      if (this.mortgageApplication.isResidential) {
        this.EPCRatingOptionList = this.referenceDataService.getOptionListConfigItems(
          this.referenceDataOptionLists.EPCRatingResidentialRemortgage
        );
      } else {
        this.EPCRatingOptionList = this.referenceDataService.getOptionListConfigItems(
          this.referenceDataOptionLists.EPCRatingBTLRemortgage
        );
      }
    }

    this.EPCRatingOptionList = moveConfigItemToFirstPosition(
      this.EPCRatingOptionList,
      this.referenceDataOptionLists.EPCRatingUnknown
    );

    if (this.mortgageApplication?.IsFullApp) {
      this.EPCRatingOptionList = this.EPCRatingOptionList.filter(
        (element) => element.Code !== this.referenceDataOptionLists.EPCRatingUnknown
      );
    }
  }

  public displayEPCDateIssued(): void {
    let shouldDisplayEPCDateIssuedField = this.referenceDataService.getOptionListConfigItemByCode(
      this.referenceDataOptionLists.EPCRatingPropertyDetailsCommon,
      this.mortgageApplication.EPCRating
    );

    if (!shouldDisplayEPCDateIssuedField && this.mortgageApplication.isResidential) {
      shouldDisplayEPCDateIssuedField = this.referenceDataService.getOptionListConfigItemByCode(
        this.referenceDataOptionLists.EPCRatingPropertyDetailsResidential,
        this.mortgageApplication.EPCRating
      );
    }

    if (!shouldDisplayEPCDateIssuedField && !this.mortgageApplication.isResidential) {
      shouldDisplayEPCDateIssuedField = this.referenceDataService.getOptionListConfigItemByCode(
        this.referenceDataOptionLists.EPCRatingPropertyDetailsBTL,
        this.mortgageApplication.EPCRating
      );
    }

    shouldDisplayEPCDateIssuedField
      ? (this.displayEPCDateIssuedField = true)
      : (this.displayEPCDateIssuedField = false);

    if (
      this.mortgageApplication.PropertyYearOfConstructionCode === this.referenceDataConstants.PropertyUnderConstruction
    ) {
      this.displayEPCDateIssuedField = false;
      return;
    }

    this.handleEPCRatingDateIssued();
  }

  private handleEPCRatingDateIssued(): void {
    if (!this.displayEPCDateIssuedField) {
      this.mortgageApplication.EPCDateIssued = undefined;
      if (this.mortgageApplication.FactFind2) {
        this.mortgageApplication.FactFind2.EPCDateIssued = undefined;
      }
    }
  }

  public setEPCRatingInfoMessage(): void {
    const isBTLWarningEPCSelected = this.referenceDataService.getOptionListConfigItemByCode(
      this.referenceDataOptionLists.EPCRatingBTLWarningInfo,
      this.mortgageApplication.EPCRating
    );

    if (isBTLWarningEPCSelected && !this.mortgageApplication.isResidential) {
      this.epcRatingInfoMessage = this.referenceDataService.getLocalizeLabel(
        this.referenceDataLabels.EPCRatingBTLWarningBody
      );
    } else if (!this.mortgageApplication.isAdvisedCase && !this.isGreenProductSelected()) {
      this.epcRatingInfoMessage = this.referenceDataService.getConfigurationValueByCode(
        ReferenceDataCategoriesConstants.OPTION_LIST_CATEGORY,
        this.referenceDataOptionLists.EPCRatingEligibleGreenProductInfo,
        this.mortgageApplication?.EPCRating
      );
    }
  }

  public isGreenProductSelected(): boolean {
    const product = this.mortgageApplication.ApplicationProducts[0].CaseProduct.Product;
    if (
      product &&
      product.ProductEPCRatings.every(
        (x) =>
          x.EnergyRatingCode === this.referenceDataOptionLists.EPCRatingA ||
          x.EnergyRatingCode === this.referenceDataOptionLists.EPCRatingB
      )
    ) {
      return true;
    }

    return false;
  }

  public handleEPCRatingEPCDateAmendButtonDisplay(): void {
    this.isEPCEPCDateDisabled =
      this.mortgageApplicationFormService.getCurrentContext() === MortgageApplicationContext.Direct &&
      this.mortgageApplication.IsFullApp &&
      this.mortgageApplication.isAdvisedCase;

    if (this.mortgageApplicationFormService.getCurrentContext() === MortgageApplicationContext.Direct) {
      if (!this.mortgageApplication.isAdvisedCase) {
        this.isEPCRatingOptionListDisabled = true;
        this.isEPCRatingAmendDisplayed = true;
      } else {
        if (this.mortgageApplication.IsFullApp) {
          this.isEPCRatingOptionListDisabled = true;
          this.isEPCRatingAmendDisplayed = false;
        } else {
          this.isEPCRatingOptionListDisabled = false;
          this.isEPCRatingAmendDisplayed = false;
        }
      }
    } else {
      this.isEPCRatingOptionListDisabled = false;
    }
  }

  public onCheckProductReset(): void {
    const product = this.mortgageApplication.ApplicationProducts[0].CaseProduct.Product;
    const isBTLWarningEPCSelected = this.referenceDataService.getOptionListConfigItemByCode(
      this.referenceDataOptionLists.EPCRatingBTLWarningInfo,
      this.mortgageApplication.EPCRating
    );
    if (
      product &&
      this.mortgageApplication.EPCRating !== this.referenceDataOptionLists.EPCRatingUnknown &&
      this.mortgageApplication.EPCRating !== null &&
      product.ProductEPCRatings.length > 0 &&
      !product.ProductEPCRatings.some((x) => x.EnergyRatingCode === this.mortgageApplication.EPCRating)
    ) {
      this.mortgageApplication.ApplicationProducts[0].CaseProduct.Product = null;
      this.mortgageApplication.ApplicationProducts[0].CaseProduct.ProductID = null;
      this.isNotEPCRatingOnCurrentProduct =
        isBTLWarningEPCSelected && !this.mortgageApplication.isResidential ? false : true;
    } else {
      this.isNotEPCRatingOnCurrentProduct = false;
    }
  }

  public handleKnownPropertyDetailsCodeChange(): void {
    if (
      this.mortgageApplication.KnowPropertyDetailsCode === this.referenceDataOptionLists.AnswerTypeNo &&
      !this.isEPCRatingDisabled()
    ) {
      this.mortgageApplication.EPCRating = null;
      this.mortgageApplication.EPCDateIssued = null;
      this.displayEPCDateIssued();
    }
  }

  public isEPCRatingDisabled(): boolean {
    return (
      this.isEPCRatingOptionListDisabled ||
      this.mortgageApplication?.IsApplicationReadOnly ||
      this.mortgageApplication?.IsFactFindReadOnly
    );
  }

  public checkResetEPCDateIssuedForUnderConstruction(event: any): void {
    if (
      this.mortgageApplication.PropertyYearOfConstructionCode === this.referenceDataConstants.PropertyUnderConstruction
    ) {
      this.mortgageApplication.EPCDateIssued = null;
      if (this.mortgageApplication.FactFind2) {
        this.mortgageApplication.FactFind2.EPCDateIssued = null;
      }
    }
    this.mortgageApplication.PropertyYearOfConstructionCode = event;
  }

  public onCheckPropertyYearOfConstruction(): void {
    this.displayEPCDateIssued();
    if (
      this.mortgageApplication.PropertyYearOfConstructionCode ===
        this.referenceDataConstants.PropertyUnderConstruction &&
      this.mortgageApplication.EPCRating !== this.referenceDataOptionLists.EPCRatingUnknown &&
      this.mortgageApplication.EPCRating !== this.referenceDataOptionLists.EPCRatingExpired
    ) {
      this.mortgageApplication.EPCDateIssued = new Date();
      this.isPEARating = true;
      if (this.mortgageApplication.FactFind2) {
        this.mortgageApplication.FactFind2.EPCDateIssued = new Date();
      }
    } else if (
      this.mortgageApplication.PropertyYearOfConstructionCode !== this.referenceDataConstants.PropertyUnderConstruction
    ) {
      this.isPEARating = false;
    }
  }

  public handleEPCExpireDate(event: any): void {
    const epcDataIssued = new Date(event);
    if (this.mortgageApplication.isRemortgage && this.mortgageApplication.isResidential) {
      const epcExpirationDate = this.getEPCDateIssuedExpirationDate();
      if (epcExpirationDate > epcDataIssued) {
        this.isEPCExpiredOnRemortgage = true;
      } else {
        this.isEPCExpiredOnRemortgage = false;
      }
    }
  }

  private getEPCDateIssuedExpirationDate(): Date {
    const ePCDateIssuedMaxAgeValue = this.referenceDataService.getConfigurationValue(
      ReferenceDataCategoriesConstants.CLIENT_ENVIRONMENT_CATEGORY,
      ReferenceDataConstants.EPCDateIssuedMaxAge
    );

    const yearsAndMonthsText = ePCDateIssuedMaxAgeValue.split(',');
    const yearsText = yearsAndMonthsText[0].split(' ');
    const monthText = yearsAndMonthsText.length > 1 ? yearsAndMonthsText[1].split(' ') : [];

    const epcExpirationDate = new Date();
    epcExpirationDate.setFullYear(epcExpirationDate.getFullYear() - +yearsText[0]);

    epcExpirationDate.setMonth(
      monthText.length > 0 ? epcExpirationDate.getMonth() - +monthText[0] : epcExpirationDate.getMonth()
    );

    if (epcExpirationDate > new Date()) {
      epcExpirationDate.setFullYear(epcExpirationDate.getFullYear() - 1);
    }
    return epcExpirationDate;
  }
}
